import {
    Mail,
    Phone,
    MapPin,
    Sparkles,
    ArrowRight,
    Calendar,
    MessageCircle,
    CheckCircle2,
} from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Textarea } from './ui/Textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import {
    motion,
    useInView,
    useScroll,
    useTransform,
    useSpring,
    useReducedMotion,
} from 'motion/react';
import { useMemo, useRef, useState } from 'react';
import { useForm, ValidationError } from '@formspree/react';

// 👉 Modal import (Pfad ggf. anpassen)
import { PrivacyModal } from './PrivacyModal';

function ContactFormCard({ requestTypes, onOpenPrivacy, onNewMessage }) {
    const [state, handleSubmit] = useForm('xzddzedo');

    return (
        <Card className="relative overflow-hidden rounded-2xl border border-gray-200/70 bg-white/80 backdrop-blur shadow-xl">
            {/* glow */}
            <div className="pointer-events-none absolute -inset-1 blur-xl opacity-40 bg-gradient-to-r from-blue-500/15 via-purple-500/15 to-pink-500/15" />

            <CardHeader className="relative">
                <CardTitle className="text-2xl">Nachricht senden</CardTitle>
                <CardDescription className="text-base">
                    Kurze Anfrage reicht – je konkreter, desto besser. Ich melde mich innerhalb von 24 Stunden.
                </CardDescription>
            </CardHeader>

            <CardContent className="relative">
                {state.succeeded ? (
                    <div className="relative overflow-hidden rounded-2xl border border-emerald-200 bg-white/70 p-6 shadow-sm">
                        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.25),transparent_55%)]" />
                        <div className="relative flex items-start gap-4">
                            <div className="mt-0.5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500 text-white shadow-md">
                                <CheckCircle2 className="h-6 w-6" />
                            </div>

                            <div>
                                <div className="text-lg font-semibold text-gray-900">
                                    Ihre Nachricht wurde erfolgreich übermittelt. Ich melde mich zeitnah!
                                </div>

                                <div className="mt-2 text-sm text-gray-600">
                                    Falls Sie noch etwas ergänzen möchten, können Sie mir auch direkt schreiben:{' '}
                                    <a
                                        href="mailto:kontakt@johannsen-its.de"
                                        className="font-medium text-emerald-700 hover:text-emerald-800"
                                    >
                                        kontakt@johannsen-its.de
                                    </a>
                                </div>

                                <div className="mt-5 flex flex-wrap gap-3">
                                    <Button
                                        type="button"
                                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                                        onClick={onNewMessage}
                                    >
                                        Neue Nachricht
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>

                                    <a
                                        href="mailto:kontakt@johannsen-its.de"
                                        className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white/60 px-4 py-3 text-sm hover:bg-white transition-colors"
                                    >
                                        <MessageCircle className="h-4 w-4" />
                                        Direkt mailen
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <form className="space-y-5" onSubmit={handleSubmit}>
                        <input type="hidden" name="_subject" value="Neue Kontaktanfrage (Website)" />

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="name" className="block text-sm mb-2 text-gray-700">
                                    Name
                                </label>
                                <Input id="name" name="name" placeholder="Ihr Name" required />
                                <ValidationError prefix="Name" field="name" errors={state.errors} />
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm mb-2 text-gray-700">
                                    Email
                                </label>
                                <Input id="email" name="email" type="email" placeholder="ihre@email.de" required />
                                <ValidationError prefix="Email" field="email" errors={state.errors} />
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="requestType" className="block text-sm mb-2 text-gray-700">
                                    Wofür ist die Anfrage?
                                </label>
                                <select
                                    id="requestType"
                                    name="requestType"
                                    defaultValue=""
                                    required
                                    className="w-full h-9 rounded-md border border-input bg-input-background px-3 text-base md:text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
                                >
                                    {requestTypes.map((opt) => (
                                        <option key={opt.value} value={opt.value} disabled={opt.value === ''}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                                <ValidationError prefix="Anfrage" field="requestType" errors={state.errors} />
                            </div>

                            <div>
                                <label htmlFor="budget" className="block text-sm mb-2 text-gray-700">
                                    Budget (optional)
                                </label>
                                <select
                                    id="budget"
                                    name="budget"
                                    defaultValue=""
                                    className="w-full h-9 rounded-md border border-input bg-input-background px-3 text-base md:text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
                                >
                                    <option value="" disabled>
                                        Bitte auswählen…
                                    </option>
                                    <option value="under-1000">unter 1.000€</option>
                                    <option value="1000-2500">1.000€ – 2.500€</option>
                                    <option value="2500-5000">2.500€ – 5.000€</option>
                                    <option value="unknown">noch offen</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="subject" className="block text-sm mb-2 text-gray-700">
                                Betreff
                            </label>
                            <Input id="subject" name="subject" placeholder="Worum geht es?" required />
                            <ValidationError prefix="Betreff" field="subject" errors={state.errors} />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="timeline" className="block text-sm mb-2 text-gray-700">
                                    Zeitrahmen (optional)
                                </label>
                                <select
                                    id="timeline"
                                    name="timeline"
                                    defaultValue=""
                                    className="w-full h-9 rounded-md border border-input bg-input-background px-3 text-base md:text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
                                >
                                    <option value="" disabled>
                                        Bitte auswählen…
                                    </option>
                                    <option value="asap">so schnell wie möglich</option>
                                    <option value="2-4weeks">2–4 Wochen</option>
                                    <option value="1-2months">1–2 Monate</option>
                                    <option value="3monthsplus">3 Monate+</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="website" className="block text-sm mb-2 text-gray-700">
                                    Website/Link (optional)
                                </label>
                                <Input id="website" name="website" placeholder="z.B. https://…" />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="message" className="block text-sm mb-2 text-gray-700">
                                Nachricht
                            </label>
                            <Textarea
                                id="message"
                                name="message"
                                rows={6}
                                required
                                placeholder="Erzählen Sie mir kurz: Ziel, Umfang, wichtige Seiten/Funktionen, Beispiele die Ihnen gefallen…"
                            />
                            <ValidationError prefix="Nachricht" field="message" errors={state.errors} />
                        </div>

                        {/* Datenschutz */}
                        <div className="rounded-xl border border-gray-200/70 bg-white/60 px-4 py-3">
                            <label className="flex items-start gap-3 text-sm text-gray-700">
                                <input
                                    id="privacy"
                                    name="privacy"
                                    type="checkbox"
                                    value="accepted"
                                    required
                                    className="mt-1 h-4 w-4 rounded border-gray-300"
                                />
                                <span>
                  Ich habe die{' '}
                                    <button
                                        type="button"
                                        onClick={onOpenPrivacy}
                                        className="font-medium text-purple-700 hover:text-purple-800 underline underline-offset-2"
                                    >
                    Datenschutzerklärung
                  </button>{' '}
                                    gelesen und stimme der Verarbeitung meiner Daten zu.
                </span>
                            </label>
                            <ValidationError prefix="Datenschutz" field="privacy" errors={state.errors} />
                        </div>

                        <Button
                            type="submit"
                            disabled={state.submitting}
                            className="w-full group bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-md text-white hover:text-white disabled:opacity-70"
                        >
                            {state.submitting ? 'Wird gesendet…' : 'Nachricht senden'}
                            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </Button>

                        {state.errors?.length ? (
                            <div className="text-sm text-red-700">
                                Bitte prüfen Sie die markierten Felder und versuchen Sie es erneut.
                            </div>
                        ) : null}

                        <div className="text-xs text-gray-500 leading-relaxed">
                            Mit dem Absenden stimmen Sie zu, dass Ihre Angaben zur Bearbeitung der Anfrage gespeichert werden.
                        </div>
                    </form>
                )}
            </CardContent>
        </Card>
    );
}

export function Contact() {
    const ref = useRef(null);
    const reduceMotion = useReducedMotion();
    const isInView = useInView(ref, { once: true, margin: '-120px' });

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'end start'],
    });

    const meshYRaw = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
    const meshScaleRaw = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.08, 1]);
    const meshY = useSpring(meshYRaw, { stiffness: 85, damping: 22 });
    const meshScale = useSpring(meshScaleRaw, { stiffness: 85, damping: 22 });

    const contactInfo = useMemo(
        () => [
            {
                icon: Mail,
                title: 'Email',
                value: 'kontakt@johannsen-its.de',
                link: 'mailto:kontakt@johannsen-its.de',
            },
            {
                icon: Phone,
                title: 'Telefon',
                value: '+49 151 17 80 6720',
                link: 'tel:+4915117806720',
            },
            {
                icon: MapPin,
                title: 'Standort',
                value: 'Rhauderfehn, Deutschland',
                link: null,
            },
        ],
        []
    );

    const requestTypes = useMemo(
        () => [
            { value: '', label: 'Bitte auswählen…' },
            { value: 'website', label: 'Website / Relaunch' },
            { value: 'landingpage', label: 'Landing Page (Conversion)' },
            { value: 'webapp', label: 'Web-App / Dashboard / CRM' },
            { value: 'design', label: 'UI/UX / Look & Feel / Design System' },
            { value: 'performance', label: 'Performance & SEO Optimierung' },
            { value: 'maintenance', label: 'Wartung / Support / Updates' },
            { value: 'consulting', label: 'Beratung (Strategie / Konzept)' },
            { value: 'other', label: 'Sonstiges' },
        ],
        []
    );

    const [privacyOpen, setPrivacyOpen] = useState(false);

    // remount des Formulars (setzt Formspree state zurück) ohne Reload
    const [formKey, setFormKey] = useState(0);

    return (
        <section id="kontakt" className="relative py-24 px-4 overflow-hidden" ref={ref}>
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100" />

            {/* Animated mesh */}
            <motion.div
                className="absolute inset-0 opacity-30"
                style={{
                    background:
                        'radial-gradient(circle at 30% 50%, rgba(59, 130, 246, 0.45), transparent 55%), radial-gradient(circle at 70% 50%, rgba(168, 85, 247, 0.45), transparent 55%)',
                    y: meshY,
                    scale: meshScale,
                }}
                animate={reduceMotion ? undefined : { opacity: [0.22, 0.34, 0.22] }}
                transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />

            {/* Subtle grid */}
            <div
                className="absolute inset-0 opacity-[0.06]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(0,0,0,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.2) 1px, transparent 1px)',
                    backgroundSize: '64px 64px',
                    maskImage: 'radial-gradient(circle at 45% 25%, black, transparent 70%)',
                }}
                aria-hidden="true"
            />

            <div className="relative z-10 max-w-7xl mx-auto">
                {/* Header */}
                <motion.div
                    className="text-center mb-14"
                    initial={{ opacity: 0, y: 26 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                    <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-border/60 bg-white/70 backdrop-blur px-4 py-2 shadow-sm">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-purple-600 text-white">
              <Sparkles className="h-4 w-4" />
            </span>
                        <span className="text-sm text-gray-700">Kontakt</span>
                        <span className="ml-1 rounded-full bg-black/5 px-2 py-0.5 text-xs text-gray-700">
              Antwort meist &lt; 24h
            </span>
                    </div>

                    <h2 className="mt-6 text-5xl md:text-6xl tracking-tight">
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Lassen Sie uns sprechen
            </span>
                    </h2>
                    <p className="mt-5 text-xl text-gray-600 max-w-2xl mx-auto">
                        Je mehr Infos Sie direkt mitgeben, desto schneller kann ich Ihnen eine klare Einschätzung &amp; ein Angebot
                        machen.
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-8 items-start">
                    {/* Form */}
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                    >
                        <ContactFormCard
                            key={formKey}
                            requestTypes={requestTypes}
                            onOpenPrivacy={() => setPrivacyOpen(true)}
                            onNewMessage={() => setFormKey((k) => k + 1)}
                        />
                    </motion.div>

                    {/* Info column */}
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.15, ease: 'easeOut' }}
                    >
                        <div className="space-y-6">
                            {contactInfo.map((info, index) => {
                                const Icon = info.icon;
                                return (
                                    <motion.div
                                        key={info.title}
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                                        transition={{ duration: 0.5, delay: 0.25 + index * 0.1 }}
                                        whileHover={{ y: -2 }}
                                    >
                                        <Card className="overflow-hidden rounded-2xl border border-gray-200/70 bg-white/80 backdrop-blur shadow-lg hover:shadow-xl transition-shadow">
                                            <CardContent className="p-6">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                                                        <Icon className="w-6 h-6 text-white" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm text-gray-600">{info.title}</div>
                                                        {info.link ? (
                                                            <a href={info.link} className="text-lg hover:text-purple-700 transition-colors">
                                                                {info.value}
                                                            </a>
                                                        ) : (
                                                            <div className="text-lg text-gray-900">{info.value}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })}

                            {/* Big CTA card */}
                            <motion.div
                                className="relative overflow-hidden rounded-2xl border border-gray-200/70 bg-gradient-to-br from-blue-600 to-purple-600 text-white shadow-xl"
                                initial={{ opacity: 0, y: 18 }}
                                animate={isInView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.6, delay: 0.55 }}
                            >
                                <div className="absolute inset-0 opacity-35 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.35),transparent_55%)]" />
                                <div className="relative p-7">
                                    <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs w-fit">
                                        <Calendar className="h-4 w-4" />
                                        Kostenloses Erstgespräch
                                    </div>

                                    <h3 className="mt-4 text-2xl font-semibold">15–20 Minuten reichen.</h3>
                                    <p className="mt-2 text-white/85 leading-relaxed">
                                        Wir klären Ziele, Umfang und den besten Weg. Danach erhalten Sie eine klare Einschätzung inkl. Next
                                        Steps.
                                    </p>

                                    <div className="mt-5 flex flex-wrap gap-3">
                                        <Button variant="secondary" className="bg-white text-purple-700 hover:bg-gray-100 shadow-md">
                                            Termin vereinbaren
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </Button>

                                        <a
                                            href="mailto:kontakt@johannsen-its.de"
                                            className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-4 py-3 text-sm hover:bg-white/15 transition-colors"
                                        >
                                            <MessageCircle className="h-4 w-4" />
                                            Direkt mailen
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Datenschutz Modal */}
            <PrivacyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
        </section>
    );
}
